import { useState } from 'react'
import { Alert, Anchor, Button, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core'

// #1078 認証系画面(ログイン/パスワード再設定/確認メール再送/パスワード設定/仮登録完了)の共通カード。
// 見た目はここで完結させ、入力値・送信先・エラーは Rails 側(AuthHelper#auth_card)から props で受け取る。
// フォームは素の <form method="post"> なので Devise のコントローラにそのまま届く
// (PUT/PATCH は Rails の慣習どおり hidden の _method で送る)。
export default function AuthCard({
  logoSrc,
  logoHref,
  title,
  description,
  form,
  oauth = [],
  message,
  errors = [],
  notice,
  success,
  alert,
  links = [],
  size = 'sm',
}) {
  const hasError = Boolean(alert) || errors.length > 0

  return (
    <div className={`auth-card auth-card--${size}`}>
      <div className="auth-card_brand">
        <a href={logoHref || '/'} aria-label="Bizlink">
          <img src={logoSrc} alt="Bizlink" height={32} />
        </a>
      </div>

      <Paper withBorder radius="lg" shadow="xs" className="auth-card_panel">
        <Stack gap="lg">
          <div>
            <Title order={1} size="h3">{title}</Title>
            {description && (
              <Text size="sm" c="dimmed" mt={6} lh={1.7}>
                {description}
              </Text>
            )}
          </div>

          {success && (
            <Alert color="teal" variant="light" radius="md">
              {success}
            </Alert>
          )}
          {notice && (
            <Alert color="brand" variant="light" radius="md">
              {notice}
            </Alert>
          )}
          {hasError && (
            <Alert color="red" variant="light" radius="md" title={alert || undefined}>
              {errors.length > 0 && (
                <Stack gap={2}>
                  {errors.map((e) => (
                    <Text key={e} size="sm">{e}</Text>
                  ))}
                </Stack>
              )}
            </Alert>
          )}

          {message && (
            <Text size="sm" lh={1.8} style={{ whiteSpace: 'pre-line' }}>
              {message}
            </Text>
          )}

          {oauth.map((provider) => (
            <div key={provider.action}>
              <form method="post" action={provider.action}>
                <CsrfField />
                <Button type="submit" fullWidth size="md" variant="default">
                  {provider.label}
                </Button>
              </form>
              {provider.note && (
                <Text size="xs" c="dimmed" ta="center" mt={4} lh={1.5}>
                  {provider.note}
                </Text>
              )}
              {provider.note_links && (
                <Text size="xs" c="dimmed" ta="center" lh={1.5}>
                  {provider.note_links.map((l, i) => (
                    <span key={l.href}>
                      {i > 0 && '　/　'}
                      <Anchor href={l.href} target="_blank" rel="noopener noreferrer" size="xs">
                        {l.label}
                      </Anchor>
                    </span>
                  ))}
                </Text>
              )}
            </div>
          ))}

          {form && <AuthForm form={form} />}

          {links.length > 0 && (
            <Stack gap={8} align="center">
              {links.map((l) => (
                <Anchor key={l.href + l.label} href={l.href} size="sm">
                  {l.label}
                </Anchor>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </div>
  )
}

function AuthForm({ form }) {
  const method = (form.method || 'post').toLowerCase()

  return (
    <form method="post" action={form.action} className="auth-form" noValidate={false}>
      <CsrfField />
      {method !== 'post' && <input type="hidden" name="_method" value={method} />}
      {(form.hidden || []).map((h) => (
        <input key={h.name} type="hidden" name={h.name} value={h.value ?? ''} />
      ))}

      <Stack gap="md">
        {(form.fields || []).map((f) => {
          const common = {
            name: f.name,
            label: f.label,
            description: f.description,
            placeholder: f.placeholder,
            defaultValue: f.value ?? '',
            required: Boolean(f.required),
            autoFocus: Boolean(f.autoFocus),
            autoComplete: f.autoComplete,
            readOnly: Boolean(f.readOnly),
            size: 'md',
            error: f.error,
          }
          if (f.type === 'password') {
            if (f.requirements?.length) {
              return <PasswordWithRequirements key={f.name} field={f} common={common} />
            }
            return <PasswordInput key={f.name} {...common} />
          }
          return <TextInput key={f.name} {...common} type={f.type || 'text'} />
        })}

        <Button type="submit" fullWidth size="md" mt="xs">
          {form.submitLabel || '送信する'}
        </Button>
      </Stack>
    </form>
  )
}

// 要件を満たしているか入力中にリアルタイムで可視化するパスワード欄。
// requirements は Rails 側の CognitoPasswordPolicy::REQUIREMENTS を props で受け取り、
// pattern を JS の RegExp として評価する。表示条件はサーバ側の検証と同一ソース。
function PasswordWithRequirements({ field, common }) {
  const { defaultValue, ...inputProps } = common
  const [value, setValue] = useState(defaultValue)

  return (
    <div>
      <PasswordInput
        {...inputProps}
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
      />
      <Stack gap={4} mt={6} component="ul" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {field.requirements.map((r) => {
          const met = new RegExp(r.pattern).test(value)
          return (
            <Text key={r.label} component="li" size="xs" c={met ? 'teal' : 'dimmed'}>
              {met ? '✓' : '○'} {r.label}
            </Text>
          )
        })}
      </Stack>
    </div>
  )
}

// CSRF トークンはレイアウトの csrf_meta_tags から読む(AdminInterviewSummary と同じ)
function readCsrfMeta(name) {
  if (typeof document === 'undefined') return null
  const meta = document.querySelector(`meta[name="csrf-${name}"]`)
  return meta ? meta.content : null
}

function CsrfField() {
  const csrfParam = readCsrfMeta('param')
  const csrfToken = readCsrfMeta('token')
  if (!csrfParam || !csrfToken) return null
  return <input type="hidden" name={csrfParam} value={csrfToken} />
}
